import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import openNotification from '../../components/Common/OpenNotification';
import axiosInstance from '../../utils/axiosInstance';
import { ADD_ANTICIPATED_ORDER, ADMIN_GET_GRAPH_TOP_TEN_PROJECTS, ADMIN_PROJECT_WISE_REPORT, ADMIN_EXECUTION_REPORT, ADMIN_BUDGET_REPORT, ADMIN_YEARLY_GRAPH, ADMIN_UNIT_WISE_REPORT, ADMIN_GET_UNIT_REGION_ALL_UNITS, ADMIN_GET_UNIT_REGION_GRAPH_PROFITABILITY_CASH_PERFORMANCE, ADMIN_GET_UNIT_REGION_PROJECTS, ADMIN_PROJECT_DETAILS_PROJECT_WISE, DELETE_ANTICIPATED_ORDER, GET_ANTICIPATED_ORDER, GET_ANTICIPATED_ORDER_INPUT, GET_ANTICIPATED_ORDER_SUMMARY, GET_BUSINESS_SECTOR, GET_DIRECT_ORDER, GET_DIRECT_ORDER_DATES, GET_DIRECT_ORDER_INPUT, GET_DIRECT_ORDER_SUMMARY, UNIT_ADD_PRODUCT, UNIT_ADD_PRODUCT_BILLING, UNIT_PRODUCT_LISTING, UNIT_PRODUCT_TYPE, UPDATE_ANTICIPATED_ORDER, UNIT_GET_DIRECT_MATERIAL_GRAPH, ADMIN_GET_UNIT_REGION_DIRECT_ORDERS, ADMIN_GET_UNIT_REGION_ANTICIPATED_ORDERS, GET_ANTICIPATED_ORDER_SUMMARY_VERSIONED, GET_ANTICIPATED_ORDER_INPUT_VERSIONED, GET_DIRECT_ORDER_SUMMARY_VERSIONED, GET_DIRECT_ORDER_INPUT_VERSIONED, GET_DIRECT_ORDER_VERSIONED, ADMIN_ACUTAL_BUDGET_REPORT, ADMIN_OTHER_PROJECT_EXECUTION_PLAN, ADMIN_ALL_PROJECT_EXECUTION_PLAN, ADMIN_BUSINESS_SECTOR_WISE_EXECUTION_PLAN, ADMIN_BUSINESS_SECTOR_OTHER_PROJECTS, UNIT_GET_NON_INDAS_PROJECTS, UNIT_POST_NON_INDAS_PROJECT, GET_IUT, GET_IUT_SUMMARY, GET_IUT_INPUT, GET_IUT_DATES, ADMIN_IUT_UNIT_WISE_REPORT } from '../../api/endpoints';
import { getBillingCost, getProjectCost } from '../budgetSlice/budgetSlice';
import { RemoveEmptyKey } from '../../utils/removeEmptyKey';

// "DIRECT_MATERIAL" -> "Direct Material", "REVENUE" -> "Revenue" - used to
// build human-readable report-load toast messages from a raw FILTER_PARAMETER.
const filterLabel = (value) =>
        (value || '')
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b\w/g, (c) => c.toUpperCase());

// What (if anything) gets named in the "data loaded" toast is a choice the
// CALLER makes, not something inferred from whether FILTER_PARAMETER was
// sent - some callers (e.g. Dashboard.jsx) send a hardcoded FILTER_PARAMETER
// just to pick a data source, with no user-facing "filter" concept:
//   - `notifyWithFilter: true`  -> "<Filter> data loaded successfully."
//   - `entityLabel: 'Dashboard'` -> "Dashboard loaded successfully."
//   - neither                   -> "Data loaded successfully."
// `payload` is the thunk's fulfilled action.payload, i.e. `response.data`
// (the API's `{ status, message, data: {...} }` body) - NOT the raw axios
// response.
const loadedMessage = (params, payload) => {
        if (params.notifyWithFilter) {
                const filterName = payload?.data?.FILTER_PARAMETER || params.FILTER_PARAMETER;
                return `${filterLabel(filterName)} data loaded successfully.`;
        }

        if (params.entityLabel) return `${params.entityLabel} loaded successfully.`;

        return 'Data loaded successfully.';
};

const initialState = {
        loading: false,
        productListingData: [],
        productType: [],
        directOrderData: [],
        directOrderDataVersioned: [],
        directSummaryData: [],
        directOrderSummaryData: [],
        directOrderSummaryDataVersioned: [],
        directOrderDates: {},
        iutData: [],
        iutSummaryData: [],
        iutDates: {},
        iutDetail: [],
        iutUnitWiseReport: [],
        // Dedicated to getProjectWiseReport (Project wise report + Dashboard,
        // which intentionally share this same thunk/field pair).
        projectWiseProjectDetails: [],
        projectWiseProjectDetailsLoading: false,
        // Tracks the requestId of the most recently DISPATCHED getProjectWiseReport
        // call, so a slower, older request (e.g. the initial REVENUE fetch) can't
        // overwrite the data once a newer filter selection has already resolved.
        projectWiseReportRequestId: null,
        // Dedicated to getUnitWiseReport (Unit wise report). This used to share
        // projectWiseProjectDetails with several other pages' thunks below, which
        // let a lingering request from one page overwrite another page's data
        // the moment it settled - each thunk now owns its own field instead.
        unitWiseReportDetails: [],
        totalUnitWiseReportDetails: [],
        // Same purpose as projectWiseReportRequestId, but for getUnitWiseReport.
        unitWiseReportRequestId: null,
        // Same purpose as unitWiseReportRequestId/projectWiseReportRequestId,
        // but for getIUTUnitWiseReport.
        iutUnitWiseReportRequestId: null,
        // Dedicated to getExecutionReport - dispatched from ExecutionPlanReport.
        executionReportDetails: [],
        totalExecutionReportDetails: [],
        // Same purpose again, but for getExecutionReport.
        executionReportRequestId: null,
        // Dedicated to getProjectDetailsProjectWise (ProjectDetailsAdmin).
        projectDetailsProjectWiseData: [],
        totalProjectDetailsProjectWiseData: [],
        projectDetailsProjectWiseRequestId: null,
        // Dedicated to getYearlyGraphData (Yearly Charts).
        yearlyGraphReportData: [],
        totalYearlyGraphReportData: [],
        yearlyGraphReportRequestId: null,
        // Dedicated to getBudgetReport (Budget Report).
        budgetReportDetails: [],
        totalBudgetReportDetails: [],
        budgetReportRequestId: null,
        // Same purpose again, for getOtherProjectsExecutionPlan/
        // getBusinessSectorWiseExecutionPlan/getBusinessSectorOtherProjects.
        otherProjectsExecutionPlanRequestId: null,
        businessSectorWiseExecutionPlanRequestId: null,
        businessSectorOtherProjectsRequestId: null,
        allProjectExecutionPlanRequestId: null,
        actualProjectWiseProjectDetails: [],
        totalProjectWiseProjectDetails: [],
        totalActualProjectWiseProjectDetails: [],
        businessSectorListWithoutAll: [],
        otherProjectsExecutionPlan: [],
        allProjectExecutionPlan: [],
        businessSectorWiseExecutionPlan: [],
        businessSectorOtherProjects: [],
        nonIndasProjects: [],
};

export const fetchProductListing = createAsyncThunk('unit/productListing', async ({ productTypeCode }, { rejectWithValue }) => {
        try {
                const response = productTypeCode === '03' ? await axiosInstance.get(`${UNIT_PRODUCT_LISTING}?PRODUCT_TYPE_CODE=${productTypeCode}`) : await axiosInstance.get(UNIT_PRODUCT_LISTING);
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const fetchProductType = createAsyncThunk('unit/productType', async ({ headType, projectType }, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(`${UNIT_PRODUCT_TYPE}?HEAD_TYPE=${headType}&TYPE=${projectType}`);
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const addProduct = createAsyncThunk('unit/addProduct', async (productData, { dispatch, rejectWithValue }) => {
        try {
                const response = await axiosInstance.post(UNIT_ADD_PRODUCT, productData);
                if (response.status === 200) {
                        dispatch(
                                getProjectCost({
                                        IO_NO: productData?.IO_NO,
                                        REV_NO: productData?.REV_NO,
                                }),
                        );
                }
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const addProductBilling = createAsyncThunk('unit/addProductBilling', async (productData, { dispatch, rejectWithValue }) => {
        try {
                const response = await axiosInstance.post(UNIT_ADD_PRODUCT_BILLING, productData);
                if (response.status === 200) {
                        dispatch(
                                getBillingCost({
                                        IO_NO: productData?.IO_NO,
                                        REV_NO: productData?.REV_NO,
                                }),
                        );
                }
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getDirectOrder = createAsyncThunk('unit/getDirectOrder', async ({ page, pageSize, search, mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;

                const response = await axiosInstance.get(GET_DIRECT_ORDER, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getDirectOrderVersioned = createAsyncThunk('unit/getDirectOrder/versioned', async ({ page, pageSize, search, mnfUnit, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (version) params.version = version;
                const response = await axiosInstance.get(GET_DIRECT_ORDER_VERSIONED, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getDirectOrderSummary = createAsyncThunk('unit/getDirectOrderSummary', async ({ mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) {
                        params.MNF_UNIT = mnfUnit;
                }
                const response = await axiosInstance.get(GET_DIRECT_ORDER_SUMMARY, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getDirectOrderSummaryVersioned = createAsyncThunk('unit/getDirectOrderSummaryVersioned', async ({ mnfUnit, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) {
                        params.MNF_UNIT = mnfUnit;
                }
                if (version) {
                        params.version = version;
                }
                const response = await axiosInstance.get(GET_DIRECT_ORDER_SUMMARY_VERSIONED, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getDirectOrderDates = createAsyncThunk('unit/getDirectOrderDates', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(GET_DIRECT_ORDER_DATES);
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});


export const getDirectOrderDetail = createAsyncThunk('unit/getDirectOrderDetail', async ({ page, pageSize, search, mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;

                const response = await axiosInstance.get(GET_DIRECT_ORDER_INPUT, {
                        params,
                });

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getDirectOrderDetailVersioned = createAsyncThunk('unit/getDirectOrderDetailVersioned', async ({ page, pageSize, search, mnfUnit, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (version) params.version = version;

                const response = await axiosInstance.get(GET_DIRECT_ORDER_INPUT_VERSIONED, {
                        params,
                });

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
{
        /*IUT*/
}
export const getIUT = createAsyncThunk('unit/getIUT', async ({ page, pageSize, search, mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;

                const response = await axiosInstance.get(GET_IUT, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getIUTSummary = createAsyncThunk('unit/getIUTSummary', async ({ mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) {
                        params.MNF_UNIT = mnfUnit;
                }
                const response = await axiosInstance.get(GET_IUT_SUMMARY, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getIUTDates = createAsyncThunk('unit/getIUTDates', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(GET_IUT_DATES);
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getIUTDetail = createAsyncThunk('unit/getIUTDetail', async ({ page, pageSize, search, mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;

                const response = await axiosInstance.get(GET_IUT_INPUT, {
                        params,
                });

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

{
        /*ANTICIPATED ORDERS*/
}
export const getBusinessSector = createAsyncThunk('unit/getBusinessSector', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(GET_BUSINESS_SECTOR);
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getNonIndasProjects = createAsyncThunk(
        "unit/getNonIndasProjects",
        async (_, { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.get(
                                UNIT_GET_NON_INDAS_PROJECTS
                        );

                        return response.data;
                } catch (error) {
                        openNotification(
                                'error',
                                'Error',
                                error?.response?.data?.message || "Something went wrong."
                        );

                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);

export const addNonIndasProject = createAsyncThunk(
        "unit/addNonIndasProject",
        async (payload, { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.post(
                                UNIT_POST_NON_INDAS_PROJECT,
                                payload
                        );

                        openNotification(
                                "success",
                                "Updated",
                                response?.data?.message ||
                                "Non-INDAS project saved successfully"
                        );

                        return response.data;
                } catch (error) {
                        openNotification(
                                "error",
                                "Error",
                                error?.response?.data?.message ||
                                "Something went wrong."
                        );

                        return rejectWithValue(
                                error?.response?.data?.message ||
                                "Something went wrong."
                        );
                }
        }
);

export const addAnticipatedOrder = createAsyncThunk('unit/addAnticipatedOrder', async (data, { dispatch, rejectWithValue }) => {
        try {
                const response = await axiosInstance.post(ADD_ANTICIPATED_ORDER, data);
                if (response.status === 200) {
                        dispatch(getAnticipatedOrder());
                }
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const updatedAnticipatedOrder = createAsyncThunk('unit/updatedAnticipatedOrder', async (data, { dispatch, rejectWithValue }) => {
        try {
                const response = await axiosInstance.post(`${UPDATE_ANTICIPATED_ORDER}${data.id}`, data);
                if (response.status === 200) {
                        dispatch(getAnticipatedOrder());
                }
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getAnticipatedOrder = createAsyncThunk('unit/getAnticipatedOrder', async ({ page, pageSize, search, business_sector, mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (business_sector) params.business_sector = business_sector.join(',');
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                const response = await axiosInstance.get(GET_ANTICIPATED_ORDER, { params });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getAnticipatedOrderSummary = createAsyncThunk('unit/getAnticipatedOrderSummary', async ({ mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;

                const response = await axiosInstance.get(GET_ANTICIPATED_ORDER_SUMMARY, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getAnticipatedOrderSummaryVersioned = createAsyncThunk('unit/getAnticipatedOrderSummaryVersioned', async ({ mnfUnit, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (version) params.version = version;

                const response = await axiosInstance.get(GET_ANTICIPATED_ORDER_SUMMARY_VERSIONED, {
                        params,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getAnticipatedOrderInput = createAsyncThunk('unit/getAnticipatedOrderInput', async (payload, { rejectWithValue }) => {
        const qs = payload ? RemoveEmptyKey(payload) : '';
        try {
                const response = await axiosInstance.post(GET_ANTICIPATED_ORDER_INPUT, {
                        ...qs,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getAnticipatedOrderInputVersioned = createAsyncThunk('unit/getAnticipatedOrderInputVersioned', async (payload, { rejectWithValue }) => {
        const qs = payload ? RemoveEmptyKey(payload) : '';
        try {
                const response = await axiosInstance.post(GET_ANTICIPATED_ORDER_INPUT_VERSIONED + `?version=${payload.version}`, {
                        ...qs,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const deleteAnticipatedOrder = createAsyncThunk('unit/deleteAnticipatedOrder', async (params, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.delete(`${DELETE_ANTICIPATED_ORDER}/${params}`);
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getRegionUnitProjectListing = createAsyncThunk('unit/getRegionUnitProjectListing', async (params, { rejectWithValue }) => {
        const payload = RemoveEmptyKey({
                page: params?.page,
                limit: params?.limit,
                MNF_UNIT: params.mnfUnits,
        });

        if (params?.monthRange) {
                const { from_date, to_date } = params.monthRange;
                if (from_date) {
                        payload.from_month = from_date.split('-')[1];
                        payload.from_year = from_date.split('-')[2];
                }

                if (to_date) {
                        payload.to_month = to_date.split('-')[1];
                        payload.to_year = to_date.split('-')[2];
                }
        }

        try {
                const response = await axiosInstance.get(ADMIN_GET_UNIT_REGION_PROJECTS, {
                        params: payload,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getRegionUnitDirectOrderListing = createAsyncThunk('unit/getRegionUnitDirectOrderListing', async (params, { rejectWithValue }) => {
        const payload = RemoveEmptyKey({
                page: params?.page,
                limit: params?.limit,
                MNF_UNIT: params.mnfUnits,
        });

        try {
                const response = await axiosInstance.get(ADMIN_GET_UNIT_REGION_DIRECT_ORDERS, {
                        params: payload,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getRegionUnitAnticipatedOrderListing = createAsyncThunk('unit/getRegionUnitAnticipatedOrderListing', async (params, { rejectWithValue }) => {
        const payload = RemoveEmptyKey({
                page: params?.page,
                limit: params?.limit,
                MNF_UNIT: params.mnfUnits,
        });

        try {
                const response = await axiosInstance.get(ADMIN_GET_UNIT_REGION_ANTICIPATED_ORDERS, {
                        params: payload,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getAllUnits = createAsyncThunk('unit/getAllUnits', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(ADMIN_GET_UNIT_REGION_ALL_UNITS);
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getYearlyGraphData = createAsyncThunk('admin/yearlyGraph', async (_, { rejectWithValue }) => {


        try {
                const response = await axiosInstance.get(ADMIN_YEARLY_GRAPH);

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getquarterlyGraphData = createAsyncThunk('admin/yearlyGraph', async (_, { rejectWithValue }) => {


        try {
                const response = await axiosInstance.get(ADMIN_YEARLY_GRAPH);

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
// NOTE: none of these three thunks call openNotification themselves anymore -
// the success/error toast for each now lives in its own `fulfilled`/`rejected`
// reducer below, gated by the same requestId check that already discards a
// stale response's DATA. Rapidly switching REVENUE -> DM -> PROJECT_COST used
// to pop 3 toasts (one per settled request) even though only the last
// filter's data ever actually rendered; now only the request matching the
// most-recently-dispatched requestId gets to toast, same as it gets to write
// state.
export const getUnitWiseReport = createAsyncThunk('admin/consolidatedReport', async (params, { rejectWithValue }) => {


        try {
                const response = await axiosInstance.get(ADMIN_UNIT_WISE_REPORT, {
                        params: {
                                FILTER_PARAMETER: params.FILTER_PARAMETER,
                        }
                });

                return response.data;
        } catch (error) {
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getIUTUnitWiseReport = createAsyncThunk('admin/iutUnitWiseReport', async (params, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(ADMIN_IUT_UNIT_WISE_REPORT, {
                        params: {
                                FILTER_PARAMETER: params.FILTER_PARAMETER,
                        }
                });

                return response.data;
        } catch (error) {
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getProjectWiseReport = createAsyncThunk('admin/consolidatedProjectWiseReport', async (params, { rejectWithValue }) => {


        try {
                const response = await axiosInstance.get(ADMIN_PROJECT_WISE_REPORT, {
                        params: {
                                FILTER_PARAMETER: params.FILTER_PARAMETER,
                        }
                });

                return response.data;
        } catch (error) {
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getBudgetReport = createAsyncThunk('admin/consolidatedBudgetReport', async (params, { rejectWithValue }) => {


        try {
                const response = await axiosInstance.get(ADMIN_BUDGET_REPORT, {
                        params: {
                                from_year: params.from_year,
                                from_month: 4,
                                to_year: params.to_year,
                                to_month: 3,
                                MNF_UNIT: params?.mnf_unit,
                        }
                });

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getActualBudgetReport = createAsyncThunk('admin/consolidatedActualBudgetReport', async (params, { rejectWithValue }) => {


        try {
                const response = await axiosInstance.get(ADMIN_ACUTAL_BUDGET_REPORT, {
                        params: {
                                MNF_UNIT: params?.mnf_unit,
                        }
                });

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
// Success/error toast lives in the fulfilled/rejected reducers below (same
// as getUnitWiseReport/getIUTUnitWiseReport/getProjectWiseReport), gated by
// the requestId guard so a stale, superseded request can't toast either.
export const getExecutionReport = createAsyncThunk('admin/consolidatedExecutionReport', async (params, { rejectWithValue }) => {


        try {
                const response = await axiosInstance.get(ADMIN_EXECUTION_REPORT, {
                        params: {
                                FILTER_PARAMETER: params.FILTER_PARAMETER,
                                MNF_UNIT: params.MNF_UNIT,

                        }
                });

                return response.data;
        } catch (error) {
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
// Success/error toast for these three lives in the fulfilled/rejected
// reducers below (same as getUnitWiseReport/getExecutionReport/etc.), gated
// by a requestId guard so a stale, superseded request can't toast either.
export const getBusinessSectorWiseExecutionPlan = createAsyncThunk(
        "admin/businessSectorWiseExecutionPlan",
        async (params, { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.get(
                                ADMIN_BUSINESS_SECTOR_WISE_EXECUTION_PLAN,
                                {
                                        params: {
                                                FILTER_PARAMETER: params.FILTER_PARAMETER,
                                                BS_UNIT: params.BS_UNIT,
                                        },
                                }
                        );

                        return response.data;
                } catch (error) {
                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);
export const getBusinessSectorOtherProjects = createAsyncThunk(
        "admin/businessSectorOtherProjects",
        async (params, { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.get(
                                ADMIN_BUSINESS_SECTOR_OTHER_PROJECTS,
                                {
                                        params: {
                                                FILTER_PARAMETER: params.FILTER_PARAMETER,
                                                BS_UNIT: params.BS_UNIT,
                                        },
                                }
                        );

                        return response.data;
                } catch (error) {
                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);
export const getProfitabilityAndCashPerformanceGraphDetails = createAsyncThunk('unit/getProfitabilityCashPerformanceGraphDetails', async (params, { rejectWithValue }) => {
        const payload = RemoveEmptyKey({
                MNF_UNIT: params.mnfUnit,
        });
        try {
                const response = await axiosInstance.get(ADMIN_GET_UNIT_REGION_GRAPH_PROFITABILITY_CASH_PERFORMANCE, { params: payload });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getTopTenProjects = createAsyncThunk('unit/getTopTenProjects', async (params, { rejectWithValue }) => {
        try {
                const payload = {
                        MNF_UNIT: params.mnfUnit,
                        TYPE: params.projectType,
                };

                const response = await axiosInstance.get(ADMIN_GET_GRAPH_TOP_TEN_PROJECTS, {
                        params: payload,
                });
                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

// Success/error toast lives in the fulfilled/rejected reducers below (same
// as getBusinessSectorWiseExecutionPlan/getBusinessSectorOtherProjects above).
export const getOtherProjectsExecutionPlan = createAsyncThunk(
        "unit/getOtherProjectsExecutionPlan",
        async (params, { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.get(
                                ADMIN_OTHER_PROJECT_EXECUTION_PLAN,
                                {
                                        params,
                                }
                        );

                        return response.data;
                } catch (error) {
                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);

// Success/error toast lives in the fulfilled/rejected reducers below (same
// as getExecutionReport/getOtherProjectsExecutionPlan/etc.), gated by a
// requestId guard so a stale, superseded request can't toast either.
export const getAllProjectExecutionPlan = createAsyncThunk(
        "unit/getAllProjectExecutionPlan",
        async (params, { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.get(
                                ADMIN_ALL_PROJECT_EXECUTION_PLAN,
                                {
                                        params,
                                }
                        );

                        return response.data;
                } catch (error) {
                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);

export const getProjectDetailsProjectWise = createAsyncThunk('unit/getProjectDetailsProjectWise', async (params, { rejectWithValue }) => {
        try {
                let payload = {
                        MNF_UNIT: params.mnfUnit,
                };

                if (params.filterParameter) {
                        payload.FILTER_PARAMETER = params.filterParameter;
                }

                if (params.page) payload.page = params.page;
                if (params.pageSize) payload.limit = params.pageSize;
                if (params.search) payload.search = params.search;

                const response = await axiosInstance.get(ADMIN_PROJECT_DETAILS_PROJECT_WISE, {
                        params: payload,
                });

                return response.data;
        } catch (error) {
                openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});


const unitSlice = createSlice({
        name: 'unit',
        initialState,
        reducers: {},
        extraReducers: (builder) => {
                builder
                        // Reducer for fetch projects
                        .addCase(fetchProductListing.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(fetchProductListing.fulfilled, (state, action) => {
                                state.loading = false;
                                state.productListingData = action.payload.data.data;
                        })
                        .addCase(fetchProductListing.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch product type
                        .addCase(fetchProductType.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(fetchProductType.fulfilled, (state, action) => {
                                state.loading = false;
                                state.productType = action.payload.data;
                        })
                        .addCase(fetchProductType.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch direct order
                        .addCase(getDirectOrder.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getDirectOrder.fulfilled, (state, action) => {
                                state.loading = false;
                                state.directOrderData = action.payload.data.data;
                                state.totalPagesDirectOrder = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getDirectOrder.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getDirectOrderVersioned.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getDirectOrderVersioned.fulfilled, (state, action) => {
                                state.loading = false;
                                state.directOrderDataVersioned = action.payload.data.data;
                                state.totalPagesDirectOrderVersioned = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getDirectOrderVersioned.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch direct order summary
                        .addCase(getDirectOrderSummary.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                                state.directOrderSummaryData = [];
                        })
                        .addCase(getDirectOrderSummary.fulfilled, (state, action) => {
                                state.loading = false;
                                state.directOrderSummaryData = action.payload.data;
                        })
                        .addCase(getDirectOrderSummary.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getDirectOrderSummaryVersioned.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                                state.directOrderSummaryDataVersioned = [];
                        })
                        .addCase(getDirectOrderSummaryVersioned.fulfilled, (state, action) => {
                                state.loading = false;
                                state.directOrderSummaryDataVersioned = action.payload.data;
                        })
                        .addCase(getDirectOrderSummaryVersioned.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch direct order dates
                        .addCase(getDirectOrderDates.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getDirectOrderDates.fulfilled, (state, action) => {
                                state.loading = false;
                                state.directOrderDates = action.payload.data;
                        })
                        .addCase(getDirectOrderDates.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch direct order detail
                        .addCase(getDirectOrderDetail.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getDirectOrderDetail.fulfilled, (state, action) => {
                                state.loading = false;
                                state.directOrderDetail = action.payload.data.data;
                                state.totalPagesDirectOrderDetail = action?.payload?.data?.paginationData?.total;
                        })
                        .addCase(getDirectOrderDetail.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch IUT
                        .addCase(getIUT.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getIUT.fulfilled, (state, action) => {
                                state.loading = false;
                                state.iutData = action.payload.data.data;
                                state.totalPagesIUT = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getIUT.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch IUT summary
                        .addCase(getIUTSummary.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getIUTSummary.fulfilled, (state, action) => {
                                state.loading = false;
                                state.iutSummaryData = action.payload.data;
                        })
                        .addCase(getIUTSummary.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch IUT dates
                        .addCase(getIUTDates.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getIUTDates.fulfilled, (state, action) => {
                                state.loading = false;
                                state.iutDates = action.payload.data;
                        })
                        .addCase(getIUTDates.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch IUT detail
                        .addCase(getIUTDetail.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getIUTDetail.fulfilled, (state, action) => {
                                state.loading = false;
                                state.iutDetail = action.payload.data.data;
                                state.totalPagesIUTDetail = action?.payload?.data?.paginationData?.total;
                        })
                        .addCase(getIUTDetail.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch business sector
                        .addCase(getBusinessSector.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getBusinessSector.fulfilled, (state, action) => {
                                state.loading = false;
                                state.businessSectorList = [{ BS_UNIT_DESC: 'All' }, ...action.payload.data];
                                state.businessSectorListWithoutAll = action.payload.data;
                        })
                        .addCase(getBusinessSector.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch anticipated order
                        .addCase(getAnticipatedOrder.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getAnticipatedOrder.fulfilled, (state, action) => {
                                state.loading = false;
                                state.anticipatedOrderData = action.payload.data.data;
                                state.totalPagesAnticipatedOrder = action.payload.data.pagination.total;
                        })
                        .addCase(getAnticipatedOrder.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        //Reducer for fetch anticipated order summary
                        .addCase(getAnticipatedOrderSummary.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getAnticipatedOrderSummary.fulfilled, (state, action) => {
                                state.loading = false;
                                state.anticipatedOrderSummaryData = action.payload.data;
                        })
                        .addCase(getAnticipatedOrderSummary.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getAnticipatedOrderSummaryVersioned.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getAnticipatedOrderSummaryVersioned.fulfilled, (state, action) => {
                                state.loading = false;
                                state.anticipatedOrderSummaryDataVersion = action.payload.data;
                        })
                        .addCase(getAnticipatedOrderSummaryVersioned.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })

                        //Reducer for fetch anticipated order summary
                        .addCase(getAnticipatedOrderInput.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getAnticipatedOrderInput.fulfilled, (state, action) => {
                                state.loading = false;
                                state.anticipatedOrderInputData = action.payload.data.data;
                        })
                        .addCase(getAnticipatedOrderInput.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getAnticipatedOrderInputVersioned.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getAnticipatedOrderInputVersioned.fulfilled, (state, action) => {
                                state.loading = false;
                                state.anticipatedOrderInputDataVersioned = action.payload.data.data;
                        })
                        .addCase(getAnticipatedOrderInputVersioned.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })

                        .addCase(deleteAnticipatedOrder.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                                state.isAnticipatedOrderDeleted = false;
                        })
                        .addCase(deleteAnticipatedOrder.fulfilled, (state, action) => {
                                state.loading = false;
                                state.isAnticipatedOrderDeleted = true;
                        })
                        .addCase(deleteAnticipatedOrder.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                                state.isAnticipatedOrderDeleted = false;
                        })

                        .addCase(getRegionUnitProjectListing.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getRegionUnitProjectListing.fulfilled, (state, action) => {
                                state.loading = false;
                                state.regionUnitProjectListing = action.payload.data.data;
                                state.regionUnitTotalProjectListing = action.payload.data.total;
                        })
                        .addCase(getRegionUnitProjectListing.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getRegionUnitDirectOrderListing.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getRegionUnitDirectOrderListing.fulfilled, (state, action) => {
                                state.loading = false;
                                state.regionUnitDirectOrdersListing = action.payload.data.data;
                                state.regionUnitDirectOrdersTotal = action.payload.data.total;
                        })
                        .addCase(getRegionUnitDirectOrderListing.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getRegionUnitAnticipatedOrderListing.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getRegionUnitAnticipatedOrderListing.fulfilled, (state, action) => {
                                state.loading = false;
                                state.regionUnitAnticipatedOrdersListing = action.payload.data.data;
                                state.regionUnitAnticipatedOrdersTotal = action.payload.data.total;
                        })
                        .addCase(getRegionUnitAnticipatedOrderListing.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getAllUnits.pending, (state) => {
                                state.error = null;
                        })
                        .addCase(getAllUnits.fulfilled, (state, action) => {
                                state.allUnitsList = action.payload.data.data;
                        })
                        .addCase(getAllUnits.rejected, (state, action) => {
                                state.error = action.payload;
                        })
                        .addCase(getProfitabilityAndCashPerformanceGraphDetails.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getProfitabilityAndCashPerformanceGraphDetails.fulfilled, (state, action) => {
                                state.loading = false;
                                state.profitabilityAndCashPerformanceList = action.payload.data;
                        })
                        .addCase(getProfitabilityAndCashPerformanceGraphDetails.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })

                        .addCase(getTopTenProjects.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getTopTenProjects.fulfilled, (state, action) => {
                                state.loading = false;
                                state.topTenProjectList = action?.payload?.data;
                        })
                        .addCase(getTopTenProjects.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getProjectDetailsProjectWise.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                state.projectDetailsProjectWiseRequestId = action.meta.requestId;
                        })
                        .addCase(getProjectDetailsProjectWise.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.projectDetailsProjectWiseRequestId) return;

                                state.loading = false;
                                state.projectDetailsProjectWiseData = action?.payload?.data?.data;
                                state.totalProjectDetailsProjectWiseData = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getProjectDetailsProjectWise.rejected, (state, action) => {
                                if (action.meta.requestId !== state.projectDetailsProjectWiseRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                state.projectDetailsProjectWiseData = null;
                        })
                        .addCase(getYearlyGraphData.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                state.yearlyGraphReportRequestId = action.meta.requestId;
                        })
                        .addCase(getYearlyGraphData.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.yearlyGraphReportRequestId) return;

                                state.loading = false;
                                state.yearlyGraphReportData = action?.payload?.data;
                                state.totalYearlyGraphReportData = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getYearlyGraphData.rejected, (state, action) => {
                                if (action.meta.requestId !== state.yearlyGraphReportRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                state.yearlyGraphReportData = null;
                        })
                        .addCase(getUnitWiseReport.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                // Latest dispatch wins — any in-flight request started
                                // before this one becomes stale the moment this fires.
                                state.unitWiseReportRequestId = action.meta.requestId;
                        })
                        .addCase(getUnitWiseReport.fulfilled, (state, action) => {
                                // Discard a stale response — the user already switched
                                // filters and a newer request is in flight (or has already
                                // settled), so this older response must not overwrite it
                                // (and must not toast either — see NOTE above the thunks).
                                if (action.meta.requestId !== state.unitWiseReportRequestId) return;

                                state.loading = false;
                                state.unitWiseReportDetails = action?.payload?.data;
                                state.totalUnitWiseReportDetails = action?.payload?.data?.pagination?.total;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getUnitWiseReport.rejected, (state, action) => {
                                if (action.meta.requestId !== state.unitWiseReportRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                // Clear stale data from the previously selected filter — otherwise
                                // the old filter's numbers stay rendered under the newly selected
                                // filter's formatting (e.g. a Revenue figure gets a "%" suffix
                                // appended once the DIRECT_MATERIAL filter's request fails).
                                state.unitWiseReportDetails = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getIUTUnitWiseReport.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                // Same stale-response guard as getUnitWiseReport/
                                // getProjectWiseReport above — without it, switching
                                // filters quickly could let an older IUT response
                                // overwrite a newer one's data (and toast for it).
                                state.iutUnitWiseReportRequestId = action.meta.requestId;
                        })
                        .addCase(getIUTUnitWiseReport.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.iutUnitWiseReportRequestId) return;

                                state.loading = false;
                                state.iutUnitWiseReport = action?.payload?.data;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getIUTUnitWiseReport.rejected, (state, action) => {
                                if (action.meta.requestId !== state.iutUnitWiseReportRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                // Same rationale as getUnitWiseReport.rejected above.
                                state.iutUnitWiseReport = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getProjectWiseReport.pending, (state, action) => {
                                state.loading = true;
                                state.projectWiseProjectDetailsLoading = true;
                                state.error = null;
                                // Latest dispatch wins — any in-flight request started
                                // before this one becomes stale the moment this fires.
                                state.projectWiseReportRequestId = action.meta.requestId;
                        })
                        .addCase(getProjectWiseReport.fulfilled, (state, action) => {
                                // Discard a stale response — the user already switched
                                // filters and a newer request is in flight (or has already
                                // settled), so this older response must not overwrite it
                                // (and must not toast either — see NOTE above the thunks).
                                if (action.meta.requestId !== state.projectWiseReportRequestId) return;

                                state.loading = false;
                                state.projectWiseProjectDetailsLoading = false;
                                state.projectWiseProjectDetails = action?.payload?.data;
                                state.totalProjectWiseProjectDetails = action?.payload?.data?.pagination?.total;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getProjectWiseReport.rejected, (state, action) => {
                                if (action.meta.requestId !== state.projectWiseReportRequestId) return;

                                state.loading = false;
                                state.projectWiseProjectDetailsLoading = false;
                                state.error = action.payload;
                                // Clear stale data from the previously selected filter — otherwise
                                // the old filter's numbers stay rendered under the newly selected
                                // filter's formatting (e.g. a Revenue figure gets a "%" suffix
                                // appended once the DIRECT_MATERIAL filter's request fails).
                                state.projectWiseProjectDetails = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getBudgetReport.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                state.budgetReportRequestId = action.meta.requestId;
                        })
                        .addCase(getBudgetReport.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.budgetReportRequestId) return;

                                state.loading = false;
                                state.budgetReportDetails = action?.payload?.data;
                                state.totalBudgetReportDetails = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getBudgetReport.rejected, (state, action) => {
                                if (action.meta.requestId !== state.budgetReportRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                state.budgetReportDetails = null;
                        })
                        .addCase(getActualBudgetReport.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getActualBudgetReport.fulfilled, (state, action) => {
                                state.loading = false;
                                state.actualProjectWiseProjectDetails = action?.payload?.data;
                                state.totalActualProjectWiseProjectDetails = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getActualBudgetReport.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getExecutionReport.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                // Latest dispatch wins — any in-flight request started
                                // before this one becomes stale the moment this fires.
                                state.executionReportRequestId = action.meta.requestId;
                        })
                        .addCase(getExecutionReport.fulfilled, (state, action) => {
                                // Discard a stale response — a newer request (from this
                                // page's own filter switch, or another page sharing this
                                // same field) is in flight or has already settled, so this
                                // older response must not overwrite it.
                                if (action.meta.requestId !== state.executionReportRequestId) return;

                                state.loading = false;
                                state.executionReportDetails = action?.payload?.data;
                                state.totalExecutionReportDetails = action?.payload?.data?.pagination?.total;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getExecutionReport.rejected, (state, action) => {
                                if (action.meta.requestId !== state.executionReportRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                // Clear stale data from the previously selected filter — otherwise
                                // the old filter's numbers stay rendered under the newly selected
                                // filter's formatting.
                                state.executionReportDetails = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getOtherProjectsExecutionPlan.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                // Latest dispatch wins — any in-flight request started
                                // before this one becomes stale the moment this fires.
                                state.otherProjectsExecutionPlanRequestId = action.meta.requestId;
                        })
                        .addCase(getOtherProjectsExecutionPlan.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.otherProjectsExecutionPlanRequestId) return;

                                state.loading = false;
                                state.otherProjectsExecutionPlan = action.payload.data;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getOtherProjectsExecutionPlan.rejected, (state, action) => {
                                if (action.meta.requestId !== state.otherProjectsExecutionPlanRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                // Clear stale data from the previously selected unit — otherwise
                                // an error response (e.g. "No data found" for the newly selected
                                // unit) leaves the old unit's table rendered on screen.
                                state.otherProjectsExecutionPlan = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getAllProjectExecutionPlan.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                // Latest dispatch wins — any in-flight request started
                                // before this one becomes stale the moment this fires.
                                state.allProjectExecutionPlanRequestId = action.meta.requestId;
                        })
                        .addCase(getAllProjectExecutionPlan.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.allProjectExecutionPlanRequestId) return;

                                state.loading = false;
                                state.allProjectExecutionPlan = action.payload.data;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getAllProjectExecutionPlan.rejected, (state, action) => {
                                if (action.meta.requestId !== state.allProjectExecutionPlanRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                state.allProjectExecutionPlan = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getBusinessSectorWiseExecutionPlan.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                // Latest dispatch wins — any in-flight request started
                                // before this one becomes stale the moment this fires.
                                state.businessSectorWiseExecutionPlanRequestId = action.meta.requestId;
                        })
                        .addCase(getBusinessSectorWiseExecutionPlan.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.businessSectorWiseExecutionPlanRequestId) return;

                                state.loading = false;
                                state.businessSectorWiseExecutionPlan = action?.payload?.data;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getBusinessSectorWiseExecutionPlan.rejected, (state, action) => {
                                if (action.meta.requestId !== state.businessSectorWiseExecutionPlanRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                // Clear stale data from the previously selected filter — otherwise
                                // the old filter's numbers stay rendered under the newly selected
                                // filter's formatting.
                                state.businessSectorWiseExecutionPlan = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getBusinessSectorOtherProjects.pending, (state, action) => {
                                state.loading = true;
                                state.error = null;
                                // Latest dispatch wins — any in-flight request started
                                // before this one becomes stale the moment this fires.
                                state.businessSectorOtherProjectsRequestId = action.meta.requestId;
                        })
                        .addCase(getBusinessSectorOtherProjects.fulfilled, (state, action) => {
                                if (action.meta.requestId !== state.businessSectorOtherProjectsRequestId) return;

                                state.loading = false;
                                state.businessSectorOtherProjects = action?.payload?.data;

                                if (!action.meta.arg.silent) {
                                        openNotification('success', 'Success', loadedMessage(action.meta.arg, action.payload));
                                }
                        })
                        .addCase(getBusinessSectorOtherProjects.rejected, (state, action) => {
                                if (action.meta.requestId !== state.businessSectorOtherProjectsRequestId) return;

                                state.loading = false;
                                state.error = action.payload;
                                // Clear stale data from the previously selected filter — otherwise
                                // the old filter's numbers stay rendered under the newly selected
                                // filter's formatting.
                                state.businessSectorOtherProjects = null;

                                if (!action.meta.arg.silent) {
                                        openNotification('error', 'Error', action.payload || 'Something went wrong.');
                                }
                        })
                        .addCase(getNonIndasProjects.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getNonIndasProjects.fulfilled, (state, action) => {
                                state.loading = false;
                                state.nonIndasProjects = action?.payload?.data || [];
                        })
                        .addCase(getNonIndasProjects.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(addNonIndasProject.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(addNonIndasProject.fulfilled, (state) => {
                                state.loading = false;
                        })
                        .addCase(addNonIndasProject.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
        },
});

export default unitSlice.reducer;
